/**
 * Sensor fill-level computation.
 *
 * Given a laser-rangefinder reading `distance` (mm), the depth of the
 * monitored zone `zoneDepthMm`, and the sensor's calibration offset
 * `minDistanceMm` (the distance the sensor reads when the zone is
 * 100% full), returns a fill percentage in [0, 100], or `null` when
 * the math cannot be computed.
 *
 * Calibration intent: `minDistanceMm` accounts for fixed obstructions
 * between the sensor and the product (sensor mounted in a recessed
 * cavity, ceiling ventilation, etc.). When the sensor reads
 * `minDistanceMm`, the zone is "as full as it can physically be" —
 * i.e. 100%. When it reads `minDistanceMm + zoneDepthMm`, the zone
 * is empty (0%).
 *
 * Edge cases:
 *   - `distance == null` → `null` (no reading)
 *   - `distance == 2147483647` → `null` (device sentinel "nothing in
 *     range" — treating as 0% would falsely trigger refills for
 *     unplugged sensors)
 *   - `distance <= 0` → `null` (PD-16061: a zero depth is a module fault,
 *     not a full bin — a laser cannot measure 0 mm to the product)
 *   - `zoneDepthMm == null` or `<= 0` → `null` (sensor not calibrated)
 *   - `minDistanceMm == null` → treated as `0` (no offset). A distinct
 *     concept from "sensor not calibrated" — minDistanceMm of 0 means
 *     the sensor sits flush with the top of the zone.
 *
 * Math: fill = clamp(0, 100,
 *     100 * (zoneDepth - (distance - minDistance)) / zoneDepth
 * )
 *
 * Returns a number with 2 decimal places of precision.
 */
export const SENSOR_DISTANCE_SENTINEL_OUT_OF_RANGE = 2147483647;

/**
 * PD-15418 — plausibility margin on the FULL side. A reading can
 * legitimately land slightly above the full line (ice mounded above it),
 * so readings within this many mm closer than `minDistanceMm` still count
 * (and clamp to 100). Anything closer is physically impossible for how the
 * sensor is mounted — a misfire or a reflection — and must be discarded,
 * not clamped: a garbage 60 mm reading clamping to 100% would corrupt the
 * derived quantity and hide an empty bin.
 */
export const PLAUSIBILITY_MARGIN_MM = 75;

/**
 * PD-16061 — plausibility on the EMPTY side is proportional, not fixed: a
 * reading is over-range when it exceeds the empty-floor distance
 * (`minDistanceMm + zoneDepthMm`) by more than this factor. Production
 * data (Motor City Ice, 2026-09) showed modules parked 20–30 mm past
 * their calibrated floor in every reading — inside a fixed 75 mm margin,
 * so they clamped to 0% and read as a permanently empty bin instead of a
 * mis-calibrated or faulted module. Ten percent of the floor distance
 * still tolerates a surface dip on a deep chest while catching a module
 * that is measuring something other than the product. The same bound
 * drives the `depth_out_of_range` sensor fault, so a reading the fault
 * engine flags is by construction never fed into fill.
 */
export const DEPTH_OVER_RANGE_FACTOR = 1.1;

/**
 * The empty-floor distance (mm) for a calibrated sensor: the reading when
 * the zone holds nothing. Null when the sensor has no usable zone depth.
 */
export function emptyFloorDistanceMm(
	zoneDepthMm: number | null | undefined,
	minDistanceMm: number | null | undefined,
): number | null {
	if (zoneDepthMm == null || zoneDepthMm <= 0) {
		return null;
	}
	return (minDistanceMm ?? 0) + zoneDepthMm;
}

/**
 * PD-16061 — whether a distance reads further than the empty floor allows
 * (`(minDistanceMm + zoneDepthMm) * DEPTH_OVER_RANGE_FACTOR`). Uncalibrated
 * sensors cannot be over-range. Null/sentinel/zero readings are not
 * "over-range" either — they are their own fault classes.
 */
export function isDistanceOverRange(
	distance: number | null | undefined,
	zoneDepthMm: number | null | undefined,
	minDistanceMm: number | null | undefined,
): boolean {
	if (distance == null || distance === SENSOR_DISTANCE_SENTINEL_OUT_OF_RANGE) {
		return false;
	}
	const floor = emptyFloorDistanceMm(zoneDepthMm, minDistanceMm);
	if (floor == null) {
		return false;
	}
	return distance > floor * DEPTH_OVER_RANGE_FACTOR;
}

/**
 * PD-15418 — settling tolerance. Ice's irregular surface jitters a few mm
 * between submissions even at rest; two readings within this tolerance are
 * "the same level". Initial value chosen from live production submissions
 * (~900–1400 mm readings); tune against recorded data if needed.
 */
export const LEVEL_AGREEMENT_TOLERANCE_MM = 30;

/**
 * Whether a raw distance reading is physically possible for a calibrated
 * sensor: within [minDistance − PLAUSIBILITY_MARGIN_MM,
 * (minDistance + zoneDepth) × DEPTH_OVER_RANGE_FACTOR]. Uncalibrated
 * sensors (no zone depth) can't bound anything → plausible.
 */
export function isPlausibleDistance(
	distance: number,
	zoneDepthMm: number | null | undefined,
	minDistanceMm: number | null | undefined,
): boolean {
	if (zoneDepthMm == null || zoneDepthMm <= 0) {
		return true;
	}

	const minDist = minDistanceMm ?? 0;

	return (
		distance >= minDist - PLAUSIBILITY_MARGIN_MM &&
		!isDistanceOverRange(distance, zoneDepthMm, minDistanceMm)
	);
}

/**
 * PD-15418 — settling: a new level only counts once consecutive readings
 * agree. Devices submit roughly every 10 minutes (cellular cost — the
 * cadence will not materially increase), so "settled" means the current
 * reading agrees with the immediately-previous valid reading within
 * tolerance (~20 minutes of stability). While readings are moving — ice
 * being scooped, the surface shifting — the last established level holds.
 *
 * `previousDistance` is the sensor's previous VALID (plausible, non-null,
 * non-sentinel) reading; null means there is no prior reading to agree
 * with, so nothing establishes yet.
 */
export function isEstablishedLevel(
	distance: number,
	previousDistance: number | null | undefined,
	toleranceMm: number = LEVEL_AGREEMENT_TOLERANCE_MM,
): boolean {
	if (previousDistance == null) {
		return false;
	}

	return Math.abs(distance - previousDistance) <= toleranceMm;
}

export function computeFillLevel(
	distance: number | null | undefined,
	zoneDepthMm: number | null | undefined,
	minDistanceMm: number | null | undefined,
): number | null {
	if (distance == null) {
		return null;
	}

	if (distance === SENSOR_DISTANCE_SENTINEL_OUT_OF_RANGE) {
		return null;
	}

	// PD-16061: a zero (or negative) depth is a module fault, not a full bin.
	if (distance <= 0) {
		return null;
	}

	if (zoneDepthMm == null || zoneDepthMm <= 0) {
		return null;
	}

	// PD-15418: physically impossible readings are noise, not 0%/100%.
	if (!isPlausibleDistance(distance, zoneDepthMm, minDistanceMm)) {
		return null;
	}

	const minDist = minDistanceMm ?? 0;
	const productDepthFromTop = distance - minDist;
	const fillFraction = (zoneDepthMm - productDepthFromTop) / zoneDepthMm;
	const clamped = Math.max(0, Math.min(1, fillFraction));

	return Math.round(clamped * 100 * 100) / 100;
}
